import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import { SmallLoader } from 'shared/components';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import { useSwitchToOtherAccount } from 'src/hooks/use-switch-to-other-account';

import { detectLoyaltyIntegration, useLoyalty, Statuses } from 'src/components/loyalty/use-loyalty';
import { Connect } from 'src/components/loyalty/connect';
import { Terms } from 'src/components/loyalty/terms';
import { InlinePhoneVerification } from 'src/components/loyalty/modal/inline-phone-verification';
import { DposMyReferral } from 'src/components/loyalty/rewards/dpos-referrals';
import { useDposLoyalty } from '../../dpos-loyalty-context';

export const ReferralsPanel = (): JSX.Element => {
  const { dispensary } = useDispensary();
  const User = useUser();
  const customerId = useObserver(() => User.id);
  const phone = useObserver(() => User.phone);
  const switchToOtherAccount = useSwitchToOtherAccount();
  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const { status, setStatus, programName, programDescription, createUserAndPin } = useLoyalty({
    initialStatus: Statuses.loading,
    integration: loyaltyProgram,
    surface: 'referralsPanel',
  });

  const { referral, referralConfig, isLoyaltyMember, loyaltyRequireOptIn, loading, isFetching } = useDposLoyalty();
  const initializationKey = `${customerId}:${dispensary.id}:${loyaltyProgram}`;
  const initializedContextRef = useRef<string | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (loading) {
      return;
    }

    const nextStatus = !isLoyaltyMember && loyaltyRequireOptIn ? Statuses.signup : Statuses.success;
    if (initializedContextRef.current !== initializationKey) {
      if (isFetching) {
        return;
      }
      initializedContextRef.current = initializationKey;
      setStatus(nextStatus);
    } else if (statusRef.current === Statuses.signup && nextStatus === Statuses.success) {
      setStatus(Statuses.success);
    }
  }, [initializationKey, isFetching, isLoyaltyMember, loyaltyRequireOptIn, loading, setStatus]);

  return (
    <>
      {status === Statuses.loading && <SmallLoader data-testid='small-loader' />}

      {status === Statuses.signup && (
        <Connect setStatus={setStatus} programName={programName} programDescription={programDescription} />
      )}

      {status === Statuses.terms && (
        <Terms setStatus={setStatus} createUserAndPin={createUserAndPin} programName={programName} />
      )}

      {status === Statuses.verificationRequired && (
        <InlinePhoneVerification
          dispensaryId={dispensary.id}
          enterpriseId={dispensary.retailer?.enterpriseId}
          initialPhone={phone}
          onVerified={() => {
            void createUserAndPin({ afterVerification: true });
          }}
          onClose={() => setStatus(Statuses.signup)}
          onLoginToOtherAccount={() => {
            void switchToOtherAccount();
          }}
        />
      )}

      {status === Statuses.success && referral && (
        <ReferralsPanelContainer>
          <DposMyReferral referral={referral} referralConfiguration={referralConfig ?? undefined} hideBorder />
        </ReferralsPanelContainer>
      )}
    </>
  );
};

const ReferralsPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 50px;
`;
